import React from 'react';
import Head from 'next/head';
import moment from 'moment';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Chip from '@mui/material/Chip';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Paper from '@mui/material/Paper';

import JobDetail from '../components/jobs/JobDetail';
import Layout from '../components/layout';
import { getCompanyWithJobsAndNewsApiMethod } from '../lib/api';
import { Tab } from '@mui/material';
import Tabs from '@mui/material/Tabs';

type Company = {
  _id: string;
  name: string;
  logo: string;
  employeeRange: string;
  fundingRange: string;
  location: string;
  foundationYear: string;
  websiteUrl: string;
  jobBoardUrl: string;
  scriptName: string;
  scriptId: string;
  companySlug: string;
  linkedinUrl: string;
  description: string;
  funding: string;
  yearFounded: string;
  city: string;
  state: string;
  therArea: string;
  drugTypes: string;
  drugNotes: string;
  clinicalStage: string;
  business: string;
  investors: string;
  leadProduct: string;
  genEmail: string;
  format: string;
};

type Job = {
  _id: string;
  title: string;
  jobUrl: string;
  location: string;
  datePosted: Date;
  validThrough: Date;
  companyName: string;
  companyLogo: string;
  companyId: string;
  isJobRaw: boolean;
  scriptId: string;
  categorySlug: string;
  categoryId: string;
  state: string;
  companySize: string;
  employmentType: string;
  estimatedSalary: { minValue: string; maxValue: string };
  isHighlighted: boolean;
  isPublished: boolean;
  companySlug: string;
  companyWebsite: string;
};

type News = {
  _id: string;
  title: string;
  link: string;
  category: string;
  source: string;
  postedAt: Date;
};

type Props = {
  isThemeDark: boolean;
  currentRoute: string;
  jobs: Job[];
  news: News[];
  company: Company;
};

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

class CompanyPage extends React.Component<Props, { tab: number }> {
  public static async getInitialProps(ctx) {
    // console.log('MainPage.getInitialProps');

    const { companySlug } = ctx.query;

    const data = await getCompanyWithJobsAndNewsApiMethod(
      companySlug,
      ctx.req ? ctx.req.headers : {},
    );

    if (data && !data.error) {
      const { company, jobs, news } = data;

      return {
        company,
        jobs,
        news,
      };
    } else {
      return { error: data.error };
    }
  }

  constructor(props) {
    super(props);

    this.state = { tab: 0 };
  }

  public handleChange = (_: React.SyntheticEvent, newValue: number) => {
    this.setState({ tab: newValue });
  };

  renderCategory = (category: string) => {
    if (!category) {
      return null;
    }

    let color = '';

    if (category === 'Funding-related') {
      color = 'blue';
    }

    if (category === 'Science/business updates') {
      color = '#f4086f';
    }

    if (category === 'New hires') {
      color = 'purple';
    }

    return <span style={{ color, fontWeight: 'bold' }}>{category}</span>;
  };

  highlightText = (text, query) => {
    if (!query) return text;

    const regex = new RegExp(`(${query})`, 'gi');

    return text
      .split(regex)
      .map((part, index) =>
        part.toLowerCase() === query.toLowerCase() ? <mark key={index}>{part}</mark> : part,
      );
  };

  public render() {
    // get data
    const { company, jobs, news } = this.props;

    const ldJson = {
      '@context': 'https://schema.org/',
      '@type': 'Organization',
      name: company.name,
      url: company.websiteUrl,
      logo: company.logo,
      sameAs: [company.linkedinUrl],
    };

    if (!company) {
      return (
        <Layout {...this.props}>
          <div style={{ minHeight: '100%' }}>
            <Head>
              {' '}
              <title>Not found</title>
              <meta name="robots" content="noindex" />
            </Head>{' '}
            <Box
              sx={{
                margin: { xs: '20px 2%', md: '40px 15%' },
                padding: '1px 10px 5px 10px',
              }}
            >
              <h3>
                This company page does not exist. Contact us at recruit@workinbiotech.com to add
                your company.
              </h3>
            </Box>
          </div>
        </Layout>
      );
    }

    const logoStyle: any = {
      maxWidth: '100%',
      height: 'auto',
      width: 'auto',
      imageRendering: '-webkit-optimize-contrast',
      marginTop: '25px',
      maxHeight: '55px',
    };

    const renderTabContent = () => {
      if (this.state.tab === 0) {
        if (jobs && jobs.length > 0) {
          return (
            <Box sx={{ marginLeft: '-20px' }}>
              {jobs.map((j) =>
                j ? (
                  <div key={j._id}>
                    <JobDetail job={j} companyInfo={company} page="company" />
                  </div>
                ) : (
                  <div key={j._id}>
                    <JobDetail job={null} companyInfo={company} page="company" />
                    <p />
                  </div>
                ),
              )}
            </Box>
          );
        }

        return null;
      }

      return (
        <>
          {news && news.length > 0 ? (
            <Box>
              {news.map((n) => (
                <Box
                  key={n._id}
                  sx={{
                    borderBottom: '1px solid #ccc',
                    paddingBottom: '10px',
                    marginTop: '20px',
                    cursor: 'pointer',
                  }}
                  onClick={() => {
                    window.open(n.link, '_blank');
                  }}
                >
                  <div style={{ marginBottom: '5px', color: 'blue' }}>
                    {this.highlightText(n.title.slice(0, 200), company.name)}
                  </div>

                  <Chip
                    size="small"
                    sx={{ marginRight: '10px' }}
                    label={<div>{n.source.toLowerCase()}</div>}
                    variant="outlined"
                  />

                  {n.postedAt && (
                    <Chip
                      size="small"
                      sx={{ marginRight: '10px' }}
                      label={<div>{moment(n.postedAt).format('MMM D, YYYY')}</div>}
                      variant="outlined"
                    />
                  )}
                  {n.category && (
                    <Chip
                      size="small"
                      label={
                        <div>
                          <strong>{this.renderCategory(n.category)}</strong>
                        </div>
                      }
                      variant="outlined"
                    />
                  )}

                  <Button
                    href={n.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    component="a"
                    variant="outlined"
                    color="primary"
                    size="small"
                    style={{
                      color: '#222',
                      border: '1px solid #666',
                      marginLeft: '10px',
                      backgroundColor: '#eee',
                    }}
                  >
                    Source <OpenInNewIcon style={{ marginLeft: '12px' }} />{' '}
                  </Button>
                </Box>
              ))}
            </Box>
          ) : null}
        </>
      );
    };

    return (
      <Layout {...this.props}>
        <div style={{ minHeight: '100%' }}>
          <Head>
            <title>{company.name}</title>
            <meta
              name="description"
              content={`${company.description} - see description, jobs, team size, funding, office location and more.`}
            />
            <meta name="robots" content="index, follow" />
            <script type="application/ld+json">{JSON.stringify(ldJson)}</script>
          </Head>

          <Box
            sx={{
              margin: { xs: '25px 2%', md: '60px 18%' },
              padding: '1px 10px 5px 10px',
            }}
          >
            {company.logo && (
              <div
                style={{
                  textAlign: 'left',
                  display: 'inline-flex',
                }}
              >
                {company.logo.includes('svg') ? (
                  <>
                    <Box component="img" sx={logoStyle} src={company.logo} alt={company.name} />
                    <svg xmlns={company.logo} style={logoStyle}></svg>
                  </>
                ) : (
                  <Box component="img" sx={logoStyle} src={company.logo} alt={company.name} />
                )}
              </div>
            )}
            <h1 style={{ display: 'inline-flex', marginLeft: '40px', verticalAlign: 'super' }}>
              {company.name}
            </h1>{' '}
            <Button
              href={`https://app.workinbiotech.com/login`}
              component="a"
              variant="contained"
              target="_blank"
              color="inherit"
              sx={{
                float: 'right',
                marginRight: { xs: '25px', md: '20px' },
                marginTop: { xs: '0px', md: '20px' },
              }}
            >
              Edit info
            </Button>
            <Grid
              container
              direction="row"
              justifyContent="space-between"
              alignItems="start"
              style={{
                width: '100%',
                height: '100%',
                maxWidth: '100%',
              }}
            >
              <Grid
                item
                lg={4}
                md={12}
                sm={12}
                xs={12}
                sx={{
                  margin: { xs: '25px', md: '30px 0px 25px 0px' },
                }}
              >
                <Paper
                  style={{
                    padding: '10px 25px ',
                  }}
                  elevation={3}
                >
                  <ul style={{ margin: 0, padding: 0, listStyle: 'none', wordBreak: 'break-word' }}>
                    <p />
                    {company.yearFounded ? (
                      <li>
                        <b style={{ fontSize: 12 }}>Founded: </b>
                        {company.yearFounded}
                      </li>
                    ) : (
                      (
                        <li>
                          <b style={{ fontSize: 12 }}>Founded: </b>
                          {company.foundationYear}
                        </li>
                      ) || null
                    )}
                    <p />
                    {company.city ? (
                      <li>
                        <b style={{ fontSize: 12 }}>Location: </b>
                        {company.city ? company.city + ', ' + company.state : ''}
                      </li>
                    ) : company.location ? (
                      <li>
                        <b style={{ fontSize: 12 }}>Location: </b>
                        {company.location}
                      </li>
                    ) : null}
                    <p />
                    {company.employeeRange ? (
                      <li>
                        <b style={{ fontSize: 12 }}>Employee range: </b>
                        {company.employeeRange}
                      </li>
                    ) : null}
                    <p />
                    {company.clinicalStage ? (
                      <li>
                        <b style={{ fontSize: 12 }}>Clinical stage: </b>
                        {company.clinicalStage}
                      </li>
                    ) : null}
                    <p />
                    {company.therArea ? (
                      <li>
                        <b style={{ fontSize: 12 }}>Therapy area: </b>
                        {company.therArea}
                      </li>
                    ) : null}
                    <p />
                    {company.drugTypes ? (
                      <li>
                        <b style={{ fontSize: 12 }}>Drug types: </b>
                        {company.drugTypes}
                      </li>
                    ) : null}
                    <p />
                    {company.leadProduct ? (
                      <li>
                        <b style={{ fontSize: 12 }}>Lead product: </b>
                        {company.leadProduct}
                      </li>
                    ) : null}
                    <p />
                    {company.funding ? (
                      <li>
                        <b style={{ fontSize: 12 }}>Funding: </b>
                        {company.funding}
                      </li>
                    ) : company.fundingRange ? (
                      <li>
                        <b style={{ fontSize: 12 }}>Funding: </b>
                        {company.fundingRange}
                      </li>
                    ) : null}
                    <p />
                    {company.investors ? (
                      <li>
                        <b style={{ fontSize: 12 }}>Investors: </b>
                        {company.investors}
                      </li>
                    ) : null}
                    <p />
                  </ul>
                  <p />
                  <br />
                  {company.websiteUrl ? (
                    <Button
                      href={company.websiteUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      component="a"
                      variant="outlined"
                      color="primary"
                      style={{
                        color: '#222',
                        border: '1px solid #666',
                      }}
                    >
                      {company.websiteUrl.includes('www')
                        ? company.websiteUrl.replace('https://www.', '').replace('/', '')
                        : company.websiteUrl.replace('https://', '').replace('/', '')}{' '}
                      <OpenInNewIcon style={{ marginLeft: '12px' }} />
                    </Button>
                  ) : null}
                  <p />
                  {company.linkedinUrl ? (
                    <Button
                      href={company.linkedinUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      component="a"
                      variant="outlined"
                      color="primary"
                      style={{
                        color: '#222',
                        border: '1px solid #666',
                      }}
                    >
                      linkedin.com <OpenInNewIcon style={{ marginLeft: '12px' }} />{' '}
                    </Button>
                  ) : null}
                  <p />
                  {company.jobBoardUrl ? (
                    <Button
                      href={company.jobBoardUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      component="a"
                      variant="outlined"
                      color="primary"
                      style={{
                        color: '#222',
                        border: '1px solid #666',
                      }}
                    >
                      job board <OpenInNewIcon style={{ marginLeft: '12px' }} />{' '}
                    </Button>
                  ) : null}
                  <p />
                  <br />
                </Paper>
              </Grid>
              <Grid
                item
                lg={8}
                md={12}
                sm={12}
                xs={12}
                sx={{
                  padding: { xs: '25px', md: '20px' },
                }}
              >
                {company.drugNotes ? (
                  <>
                    <h4 style={{ marginTop: '20px' }}>Drug notes:</h4>
                    <div
                      style={{
                        overflowWrap: 'break-word',
                        margin: '0px 20px',
                      }}
                      dangerouslySetInnerHTML={{
                        __html: company.drugNotes
                          ? company.drugNotes
                          : 'Contact us to add description: recruit@workinbiotech.com',
                      }}
                    />
                  </>
                ) : null}
                <h4 style={{ marginTop: '20px' }}>About:</h4>{' '}
                <div
                  style={{
                    overflowWrap: 'break-word',
                    margin: '0px 20px',
                    minHeight: '180px',
                  }}
                  dangerouslySetInnerHTML={{
                    __html: company.description
                      ? company.description
                      : 'Contact us to add description: recruit@workinbiotech.com',
                  }}
                />
                <p />
                <div style={{ marginTop: '20px' }}>
                  <Tabs value={this.state.tab} onChange={this.handleChange}>
                    <Tab
                      label="Jobs"
                      {...a11yProps(0)}
                      sx={{ fontSize: 14, fontWeight: this.state.tab == 0 ? 'bold' : 'normal' }}
                    />
                    <Tab
                      label="News/Insights"
                      {...a11yProps(1)}
                      sx={{ fontSize: 14, fontWeight: this.state.tab == 1 ? 'bold' : 'normal' }}
                    />
                  </Tabs>

                  {renderTabContent()}
                </div>
              </Grid>
            </Grid>
          </Box>
          <p
            style={{
              marginBottom: '200px',
            }}
          />
        </div>
      </Layout>
    );
  }
}

export default CompanyPage;
